"use client";
import { useState, useMemo } from "react";
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  Text,
  Badge,
  Button,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Flex,
  Heading,
  Grid,
  Tabs,
  IconButton,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  Divider,
  Tooltip,
} from "@chakra-ui/react";
import {
  FaTruck,
  FaTimes,
  FaUser,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaMapPin,
  FaMapMarkerAlt,
  FaFileAlt,
  FaBox,
  FaCheckCircle,
  FaClock,
  FaFileInvoice,
  FaWallet,
  FaShare,
  FaRoute as FaRouteIcon,
  FaTrash,
  FaPhone,
  FaBuilding,
  FaHome,
  FaExternalLinkAlt,
  FaSync,
} from "react-icons/fa";
import { FcShipped } from "react-icons/fc";
import { GetStatusBadge } from "./GetStatusBadge";
import { formatCurrency } from "../../../utils/formatCurrency";
import type { ShipmentDetails } from "./types";
import { downloadInvoiceForOrder } from "../../Orders/orderUtils";
import { GaraazCopyText } from "@svan-garaaz/reactsharedlib";
import { formatISOToLocalDate } from "../../../utils/dateHelpers";
import { ShipmentStatus } from "../shipment.interface";
import { InvoiceStatus } from "../../Orders/enums";
import {
  cancelableShipmentStatuses,
  deletableShipmentStatuses,
} from "./ShipmentsTable";

const statusMeta = (status: string) => {
  switch (status) {
    case "DELIVERED":
      return {
        bar: "green.300",
        dot: "green.500",
        label: "Delivered",
        labelColor: "green.600",
        icon: FcShipped,
        iconColor: "green.500",
        destBg: "green.50",
        destBorder: "green.100",
        destIcon: "green.600",
        destLabel: "DELIVER TO",
      };
    case "CANCELLED":
      return {
        bar: "red.300",
        dot: "red.500",
        label: "Cancelled",
        labelColor: "red.600",
        icon: FaTimes,
        iconColor: "red.500",
        destBg: "red.50",
        destBorder: "red.100",
        destIcon: "red.600",
        destLabel: "DELIVER TO",
      };
    case "ASSIGNED":
      return {
        bar: "purple.300",
        dot: "purple.500",
        label: "Assigned",
        labelColor: "purple.700",
        icon: FaClock,
        iconColor: "purple.500",
        destBg: "purple.50",
        destBorder: "purple.100",
        destIcon: "gray.600",
        destLabel: "DELIVER TO",
      };
    case "REQUESTED":
      return {
        bar: "orange.300",
        dot: "orange.500",
        label: "Requested",
        labelColor: "orange.700",
        icon: FaClock,
        iconColor: "orange.500",
        destBg: "orange.50",
        destBorder: "orange.100",
        destIcon: "gray.600",
        destLabel: "DELIVER TO",
      };
    case "IN_TRANSIT":
      return {
        bar: "yellow.500",
        dot: "yellow.500",
        label: "In Transit",
        labelColor: "yellow.700",
        icon: FaTruck,
        iconColor: "yellow.500",
        destBg: "yellow.50",
        destBorder: "yellow.300",
        destIcon: "gray.600",
        destLabel: "DELIVER TO",
      };
    default:
      return {
        bar: "gray.200",
        dot: "gray.300",
        label: "Pending",
        labelColor: "gray.600",
        icon: FaClock,
        iconColor: "gray.400",
        destBg: "gray.50",
        destBorder: "gray.100",
        destIcon: "gray.600",
        destLabel: "DELIVER TO",
      };
  }
};

const InfoCard = ({
  icon,
  label,
  value,
  colorScheme = "blue",
  iconBg = "blue.100",
  iconColor = "blue.600",
  children,
}: {
  icon: any;
  label?: string;
  value?: string | number;
  colorScheme?: string;
  iconBg?: string;
  iconColor?: string;
  children?: React.ReactNode;
}) => {
  const cardBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Card
      bg={cardBg}
      border="1px"
      borderColor={borderColor}
      borderRadius="xl"
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "0 8px 25px -8px rgba(0, 0, 0, 0.15)",
      }}
      cursor="pointer"
      role="group"
    >
      <CardBody p={4}>
        <HStack spacing={3}>
          <Box
            w={10}
            h={10}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg={iconBg}
            borderRadius="xl"
            transition="all 0.3s"
            _groupHover={{ transform: "scale(1.1)" }}
          >
            <Icon as={icon} color={iconColor} boxSize={5} />
          </Box>
          <VStack align="start" spacing={1} flex={1}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium">
              {label}
            </Text>
            {children || (
              <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                {value || "N/A"}
              </Text>
            )}
          </VStack>
        </HStack>
      </CardBody>
    </Card>
  );
};

const DetailSection = ({
  title,
  icon,
  iconColor = "blue.600",
  children,
  data,
}: {
  title: string;
  icon: any;
  iconColor?: string;
  children: React.ReactNode;
  data?: any;
}) => {
  const cardBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box
      bg={cardBg}
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
    >
      <HStack spacing={2} mb={4} justify="space-between" align="center">
        <HStack spacing={2}>
          <Icon as={icon} color={iconColor} boxSize={5} />
          <Heading size="xs" color="gray.900">
            {title}
          </Heading>
        </HStack>
        {data?.lastUpdatedAt && (
          <HStack spacing={2} color="gray.500">
            <Text fontSize="xs" color="black" fontWeight="medium">
              Last Updated: {data.lastUpdatedAt}
            </Text>
          </HStack>
        )}
      </HStack>
      {children}
    </Box>
  );
};

const DetailRow = ({
  label,
  value,
  copyText,
  align = "space-between",
}: {
  label: string;
  value: string | number;
  copyText?: string;
  align?: "space-between" | "center";
}) => (
  <Flex justify={align} width={"100%"} align="center">
    <Text color="gray.600" fontSize="xs" fontWeight="medium">
      {label}:
    </Text>
    <HStack spacing={2}>
      {copyText && (
        <GaraazCopyText text={copyText} popOverMessage={`${label} Copied`} />
      )}
      <Text fontWeight="medium" fontSize="xs" color="gray.900">
        {value}
      </Text>
    </HStack>
  </Flex>
);

const TimelineEvent = ({
  event,
  index,
  isLatest,
}: {
  event: ShipmentDetails["statusHistory"][0];
  index: number;
  isLatest: boolean;
}) => {
  const getEventStyle = (status: string, hasData: boolean) => {
    const statusLower = status.toLowerCase();

    let baseStyle;
    if (statusLower.includes("created")) {
      baseStyle = {
        icon: FaBox,
        completedBg: "purple.100",
        completedColor: "purple.600",
      };
    } else if (
      statusLower.includes("confirmed") ||
      statusLower.includes("booking")
    ) {
      baseStyle = {
        icon: FaCheckCircle,
        completedBg: "yellow.100",
        completedColor: "yellow.600",
      };
    } else if (statusLower.includes("assigned")) {
      baseStyle = {
        icon: FaUser,
        completedBg: "orange.100",
        completedColor: "orange.600",
      };
    } else if (statusLower.includes("picked up")) {
      baseStyle = {
        icon: FaTruck,
        completedBg: "blue.100",
        completedColor: "blue.600",
      };
    } else if (statusLower.includes("delivered")) {
      baseStyle = {
        icon: FaCheckCircle,
        completedBg: "green.100",
        completedColor: "green.600",
      };
    } else if (statusLower.includes("cancelled")) {
      baseStyle = {
        icon: FaTimes,
        completedBg: "red.100",
        completedColor: "red.600",
      };
    } else {
      baseStyle = {
        icon: FaClock,
        completedBg: "gray.100",
        completedColor: "gray.600",
      };
    }

    if (hasData) {
      return {
        bg: baseStyle.completedBg,
        color: baseStyle.completedColor,
        icon: baseStyle.icon,
        opacity: 1,
      };
    } else {
      return {
        bg: "gray.50",
        color: "gray.300",
        icon: baseStyle.icon,
        opacity: 0.6,
      };
    }
  };

  const eventStyle = getEventStyle(event.status, event.hasData || false);
  const isCompleted = event.hasData || false;

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={4}
      pb={6}
      _last={{ pb: 0 }}
    >
      <Box
        w={10}
        h={10}
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={eventStyle.bg}
        color={eventStyle.color}
        border={isCompleted ? "2px" : "2px"}
        borderColor={isCompleted ? eventStyle.color : "gray.200"}
        borderStyle={isCompleted ? "solid" : "dashed"}
        boxShadow={isLatest ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none"}
        opacity={eventStyle.opacity}
        transition="all 0.3s"
      >
        <Icon as={eventStyle.icon} boxSize={4} />
      </Box>
      <Box flex={1}>
        <Flex alignItems="center" justify="space-between" mb={1}>
          <Heading
            size="xs"
            fontWeight={isCompleted ? "semibold" : "medium"}
            color={isCompleted ? "gray.900" : "gray.500"}
          >
            {event.status}
          </Heading>
          <Text
            fontSize="sm"
            color="black"
            fontWeight={isCompleted ? "medium" : "normal"}
          >
            {event.timestamp}
          </Text>
        </Flex>
        {event.remarks && (
          <Text
            fontSize="sm"
            color={isCompleted ? "gray.500" : "gray.400"}
            fontStyle="italic"
            opacity={isCompleted ? 1 : 0.7}
          >
            {event.remarks}
          </Text>
        )}
      </Box>
    </Box>
  );
};

const OrderTableRow = ({ order }: { order: ShipmentDetails["orders"][0] }) => (
  <Tr _hover={{ bg: "gray.50" }} transition="all 0.2s">
    <Td py={4}>
      <VStack align="start" spacing={1}>
        <HStack spacing={2}>
          <HStack spacing={1} w="100%">
            <Text fontWeight="medium" color="gray.900" fontSize="xs">
              {order.orderNo}
            </Text>
            <GaraazCopyText text={order.orderNo} popOverMessage="Copied" />
          </HStack>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          {order.date}
        </Text>
      </VStack>
    </Td>

    <Td px={4} py={4} minW="150px">
      <VStack align="start" spacing={1}>
        <Text fontSize="xs" fontWeight="medium" color="gray.900">
          {order.items?.length || 0} Items
        </Text>
        <Text fontSize="xs" color="gray.600">
          Total Qty:{" "}
          {order.items?.reduce(
            (sum, item) => sum + (item.givenQuantity || 0),
            0
          ) || 0}
        </Text>
      </VStack>
    </Td>

    <Td px={4} py={4} minW="120px">
      <VStack align="start" spacing={1}>
        <Text fontSize="xs" color="gray.900" fontWeight="medium">
          {formatCurrency(Number(order.invoiceAmount))}
        </Text>
      </VStack>
    </Td>

    {/* Supplier Invoice Column */}
    <Td px={4} py={4} minW="150px">
      {order.supplierInvoiceNo ? (
        <VStack align="start" spacing={1}>
          <HStack spacing={2}>
            <VStack align="start" spacing={0.5}>
              <Text fontSize="xs" color="gray.900" fontWeight="medium">
                {order.supplierInvoiceNo}
              </Text>
            </VStack>
          </HStack>
        </VStack>
      ) : (
        <Text fontSize="xs" color="gray.400" fontStyle="italic">
          No supplier invoice
        </Text>
      )}
    </Td>

    {/* Garaaz Invoice Column */}
    <Td px={4} py={4} minW="150px">
      {order.invoiceNo && order.invoiceNo !== "-" ? (
        <VStack align="start" spacing={1}>
          <HStack spacing={2}>
            <VStack align="start" spacing={0.5}>
              <Text fontSize="xs" color="gray.900" fontWeight="medium">
                {order.invoiceNo}
              </Text>
            </VStack>
            {order.invoiceMediaId && order.invoiceMediaId !== "-" && (
              <Tooltip label="Download Garaaz Invoice">
                <IconButton
                  aria-label="Download Garaaz Invoice"
                  size="xs"
                  variant="ghost"
                  icon={<FaFileInvoice />}
                  onClick={() => {
                    downloadInvoiceForOrder(order.invoiceMediaId!);
                  }}
                  colorScheme="purple"
                  _hover={{ bg: "purple.50" }}
                />
              </Tooltip>
            )}
          </HStack>
        </VStack>
      ) : (
        <Text fontSize="xs" color="gray.400" fontStyle="italic">
          No Garaaz invoice
        </Text>
      )}
    </Td>

    <Td px={4} py={4} minW="100px">
      <Badge
        colorScheme="green"
        variant="subtle"
        px={2}
        py={1}
        borderRadius="full"
        fontSize="xs"
        fontWeight="medium"
      >
        {order.status}
      </Badge>
    </Td>
  </Tr>
);

interface ShipmentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  shipment: ShipmentDetails | null;
  onCancelShipment?: (shipmentId: string, isSelfDispatch: boolean) => void;
  onDeleteShipment?: (shipmentId: string) => void;
  onMarkDeliveredShipment?: (shipmentId: string, isSelfDispatch: boolean) => void;
}

export default function ShipmentDetailsModal({
  isOpen,
  onClose,
  shipment,
  onCancelShipment,
  onDeleteShipment,
  onMarkDeliveredShipment,
}: ShipmentDetailsModalProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.600");
  const pickupBg = useColorModeValue("blue.50", "whiteAlpha.100");
  const pickupBorder = useColorModeValue("blue.100", "whiteAlpha.200");
  const pickupTitle = useColorModeValue("blue.700", "blue.300");
  const pickupIcon = useColorModeValue("blue.600", "blue.300");
  const pickupDot = useColorModeValue("blue.500", "blue.300");

  const valueColor = useColorModeValue("gray.900", "whiteAlpha.900");
  const statusDesignMeta = statusMeta(shipment?.status || "");

  const tabs = [
    { id: "overview", name: "Overview", icon: FaTruck },
    { id: "tracking", name: "Tracking", icon: FaMapPin },
    { id: "orders", name: "Orders", icon: FaFileAlt },
    { id: "billing", name: "Billing", icon: FaFileInvoice },
  ];

  const paymentSummary = useMemo(() => {
    if (!shipment?.orders?.length) {
      return {
        paidCount: 0,
        unpaidCount: 0,
        totalCount: 0,
        paidAmount: 0,
        unpaidAmount: 0,
        totalAmount: 0,
      };
    }

    let paidCount = 0;
    let unpaidCount = 0;
    let paidAmount = 0;
    let unpaidAmount = 0;

    shipment.orders.forEach((order) => {
      const amount = Number(order.invoiceAmount) || 0;
      const isPaid = order?.invoiceStatus === InvoiceStatus.PAID;

      if (isPaid) {
        paidCount++;
        paidAmount += amount;
      } else {
        unpaidCount++;
        unpaidAmount += amount;
      }
    });

    return {
      paidCount,
      unpaidCount,
      totalCount: shipment.orders.length,
      paidAmount,
      unpaidAmount,
      totalAmount: paidAmount + unpaidAmount,
    };
  }, [shipment?.orders]);

  if (!isOpen || !shipment) return null;

  const handleShareTracking = () => {
    const trackingUrl = shipment.trackingUrl;
    const customerPhone = shipment.contactDetails.receiverPhone;
    const message = `Hi! Here's your shipment tracking link: \n\n${trackingUrl}`;
    const whatsappUrl = `https://wa.me/${customerPhone?.replace(
      /\D/g,
      ""
    )}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleTrackShipment = () => {
    const trackingUrl = shipment.trackingUrl;
    window.open(trackingUrl, "_blank");
  };

  const canCancelShipment = cancelableShipmentStatuses.includes(
    shipment.status as ShipmentStatus
  );
  const canDeleteShipment = deletableShipmentStatuses.includes(
    shipment.status as ShipmentStatus
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      isCentered
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        maxW="6xl"
        h="90vh"
        m={4}
        borderRadius="3xl"
        boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
        border="1px"
        borderColor="gray.200"
        display="flex"
        flexDirection="column"
        overflow={"scroll"}
      >
        {/* Enhanced Header */}
        <Box
          bg={
            shipment.status === ShipmentStatus.DELIVERED
              ? "linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)"
              : "linear-gradient(135deg, #4299e1 0%, #3182ce 50%, #2c5aa0 100%)"
          }
          color="white"
          p={4}
          borderTopRadius="3xl"
          position="relative"
        >
          {/* Animated background elements */}
          <Box
            position="absolute"
            top="-20px"
            right="-20px"
            w="100px"
            h="100px"
            bg="whiteAlpha.100"
            borderRadius="full"
            opacity={0.6}
          />
          <Box
            position="absolute"
            bottom="-30px"
            left="-30px"
            w="80px"
            h="80px"
            bg="whiteAlpha.100"
            borderRadius="full"
            opacity={0.4}
          />

          <Flex
            align="center"
            justify="space-between"
            position="relative"
            zIndex={1}
          >
            <HStack spacing={4} minW={0}>
              <Box
                w={12}
                h={12}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="whiteAlpha.200"
                borderRadius="xl"
                backdropFilter="blur(10px)"
                border="1px"
                borderColor="whiteAlpha.300"
                transition="all 0.3s"
                _hover={{ transform: "scale(1.05)" }}
              >
                <Icon
                  as={shipment.status === ShipmentStatus.DELIVERED ? FcShipped : FaTruck}
                  boxSize={6}
                />
              </Box>
              <VStack
                align="start"
                spacing={1}
                minW={0}
                maxW={{ base: "70vw", md: "72vw" }}
              >
                <HStack spacing={1}>
                  <Heading
                    size="md"
                    fontWeight="bold"
                    whiteSpace="normal"
                    wordBreak="break-word"
                  >
                    {shipment.shipmentNo}
                  </Heading>
                  <GaraazCopyText
                    text={shipment.shipmentNo}
                    popOverMessage="Copied"
                  />
                </HStack>
                <HStack spacing={1}>
                  <Text
                    fontSize="sm"
                    color="blue.100"
                    whiteSpace="normal"
                    wordBreak="break-word"
                  >
                    {shipment.trackingNo}
                  </Text>
                  <GaraazCopyText
                    text={shipment.trackingNo ||'-'}
                    popOverMessage="Copied"
                  />
                </HStack>
              </VStack>
            </HStack>
            <HStack spacing={4}>
              <GetStatusBadge status={shipment.status as any} size="x-small" />
              <IconButton
                aria-label="Close modal"
                icon={<FaTimes />}
                variant="ghost"
                color="whiteAlpha.800"
                _hover={{
                  color: "white",
                  bg: "whiteAlpha.200",
                  transform: "scale(1.1)",
                }}
                _active={{ transform: "scale(0.95)" }}
                transition="all 0.2s"
                onClick={onClose}
                size="lg"
                borderRadius="full"
              />
            </HStack>
          </Flex>
        </Box>

        {/* Quick Info Cards */}
        <Box p={6} bg={headerBg} borderBottom="1px" borderColor={borderColor}>
          <Grid templateColumns="2.5fr 1fr 1fr 1fr" gap={4}>
            <InfoCard
              icon={FaRouteIcon}
              colorScheme="blue"
              iconBg="blue.100"
              iconColor="blue.600"
            >
              <HStack spacing={2} w="100%">
                {/* Origin (Pickup) */}
                <Box
                  p={2}
                  borderRadius="md"
                  bg={pickupBg}
                  border="1px solid"
                  borderColor={pickupBorder}
                  transition="background 0.2s ease"
                  _hover={{ bg: pickupBg }}
                  flex={1}
                >
                  <HStack spacing={1} mb={1}>
                    <Box
                      w={2}
                      h={2}
                      borderRadius="full"
                      bg={pickupDot}
                      flexShrink={0}
                    />
                    <Text
                      fontSize="xs"
                      color={pickupTitle}
                      fontWeight="semibold"
                    >
                      PICKUP FROM
                    </Text>
                  </HStack>

                  <HStack spacing={2} pl={2} minW={0}>
                    <Icon as={FaBuilding} boxSize={3.5} color={pickupIcon} />
                    <Tooltip
                      label={shipment?.contactDetails?.senderName || "—"}
                      hasArrow
                    >
                      <Text fontSize="xs" color={valueColor} noOfLines={1}>
                        {shipment?.contactDetails?.senderName || "Supplier"}
                      </Text>
                    </Tooltip>
                  </HStack>
                </Box>

                {/* Route indicator */}
                <Box position="relative" flex={0.1}>
                  <Box
                    position="absolute"
                    top="50%"
                    bottom="50%"
                    w="20px"
                    h="2px"
                    left="50%"
                    transform="translateX(-50%)"
                    bg={statusDesignMeta.bar}
                    borderRadius="full"
                  />
                  <VStack mb={"100%"} justify="center" position="relative">
                    <Icon
                      as={statusDesignMeta.icon}
                      boxSize={3.5}
                      color={statusDesignMeta.iconColor}
                    />
                  </VStack>
                </Box>

                {/* Destination (Delivery) */}
                <Box
                  p={2}
                  borderRadius="md"
                  bg={statusDesignMeta.destBg}
                  border="1px solid"
                  borderColor={statusDesignMeta.destBorder}
                  transition="background 0.2s ease"
                  _hover={{ bg: statusDesignMeta.destBg }}
                  flex={"1"}
                >
                  <HStack spacing={2} mb={1}>
                    <Box
                      w={2}
                      h={2}
                      borderRadius="full"
                      bg={statusDesignMeta.dot}
                      flexShrink={0}
                    />
                    <Text
                      fontSize="xs"
                      color={statusDesignMeta.labelColor}
                      fontWeight="semibold"
                    >
                      {statusDesignMeta.destLabel}
                    </Text>
                  </HStack>

                  <HStack spacing={2} minW={0}>
                    <Icon
                      as={FaHome}
                      boxSize={3.5}
                      color={statusDesignMeta.destIcon}
                    />
                    <Tooltip label={shipment?.customerName || "—"} hasArrow>
                      <Text fontSize="xs" noOfLines={1} color={valueColor}>
                        {shipment?.customerName || "Customer"}
                      </Text>
                    </Tooltip>
                  </HStack>
                </Box>
              </HStack>
            </InfoCard>
            <InfoCard
              icon={FaCalendarAlt}
              label="Estimated Delivery"
              value={shipment.estimatedDelivery}
              colorScheme="green"
              iconBg="green.100"
              iconColor="green.600"
            />
            <InfoCard
              icon={FaMoneyBillWave}
              label="Invoice Amount"
              value={
                shipment.orders.length > 0
                  ? formatCurrency(paymentSummary.totalAmount)
                  : "N/A"
              }
              colorScheme="purple"
              iconBg="purple.100"
              iconColor="purple.600"
            />
            <InfoCard
              icon={FaTruck}
              label="Estimated Fare"
              value={
                shipment.billingDetails?.shippingCharges
                  ? formatCurrency(
                      shipment.billingDetails.shippingCharges / 100
                    )
                  : "N/A"
              }
              colorScheme="orange"
              iconBg="orange.100"
              iconColor="orange.600"
            />
          </Grid>
        </Box>

        <Tabs
          index={tabs.findIndex((tab) => tab.id === activeTab)}
          onChange={(index) => setActiveTab(tabs[index].id)}
          overflowY={"hidden"}
          display={"flex"}
          flexDirection={"column"}
          flex={1}
          minH={0}
        >
          <TabList maxH="100%" px={6}>
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                display="flex"
                alignItems="center"
                gap={2}
                fontSize="sm"
                fontWeight="medium"
                paddingY={3}
                paddingX={4}
                color="gray.500"
                borderRadius="lg"
                transition="all 0.3s ease"
                _hover={{
                  color: "blue.500",
                  bg: "blue.50",
                  transform: "translateY(-1px)",
                }}
                _selected={{
                  color: "blue.600",
                  borderColor: "blue.600",
                  bg: "blue.50",
                  fontWeight: "semibold",
                }}
              >
                <Icon as={tab.icon} boxSize={4} />
                {tab.name}
              </Tab>
            ))}
          </TabList>

          <TabPanels
            flex="1"
            overflowY="scroll"
            sx={{
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a8a8a8",
              },
            }}
          >
            {/* Overview Tab */}
            <TabPanel p={6} h="full">
              <Grid templateColumns="repeat(2, 1fr)" gap={6} h="full" minH={0}>
                {/* Shipment Information */}
                <VStack spacing={6} align="stretch">
                  <DetailSection title="Shipment Information" icon={FaTruck}>
                    <VStack spacing={1} align="stretch">
                      <DetailRow
                        label="Shipment Number"
                        value={shipment.shipmentNo}
                        copyText={shipment.shipmentNo}
                      />
                      <DetailRow
                        label="CRN Number"
                        value={shipment.trackingNo || '-'}
                        copyText={shipment.trackingNo}
                      />
                      <DetailRow label="Booking Date" value={shipment.date} />
                      <DetailRow
                        label="Courier Partner"
                        value={shipment.courier}
                      />
                    </VStack>
                  </DetailSection>

                  <DetailSection
                    title="Package Details"
                    icon={FaBox}
                    iconColor="green.600"
                  >
                    <VStack spacing={3} align="stretch">
                      <DetailRow
                        label="Orders Count"
                        value={`${shipment.packageDetails?.orderCount} orders`}
                      />

                      <DetailRow
                        label="Total SKU Count"
                        value={`${
                          shipment.orders?.reduce(
                            (total, order) =>
                              total + (order.items?.length || 0),
                            0
                          ) || 0
                        } SKUs`}
                      />
                      <DetailRow
                        label="Total Quantities"
                        value={`${
                          shipment.orders?.reduce(
                            (total, order) =>
                              total +
                              (order.items?.reduce(
                                (acc, item) => acc + (item.givenQuantity || 0),
                                0
                              ) || 0),
                            0
                          ) || 0
                        } items`}
                      />
                    </VStack>
                  </DetailSection>
                </VStack>

                {/* Address Information */}
                <VStack spacing={6} align="stretch">
                  <DetailSection title="Pickup Address" icon={FaMapPin}>
                    <VStack spacing={1} align="start">
                      <HStack spacing={2} color="gray.900">
                        <Icon as={FaBuilding} boxSize={3} />
                        <Text fontWeight="xs" fontSize="sm">
                          {shipment.contactDetails.senderName}
                        </Text>
                      </HStack>
                      <HStack spacing={2} color="gray.600">
                        <Icon as={FaMapPin} boxSize={3} />
                        <Text fontSize="xs">
                          {shipment.pickupAddress || "N/A"}
                        </Text>
                      </HStack>
                      <HStack spacing={2} color="gray.500">
                        <Icon as={FaPhone} boxSize={3} />
                        <Text fontSize="xs">
                          {shipment.contactDetails.senderPhone}
                        </Text>
                      </HStack>
                    </VStack>
                  </DetailSection>

                  <DetailSection
                    title="Delivery Address"
                    icon={FaMapMarkerAlt}
                    iconColor="green.600"
                  >
                    <VStack spacing={1} align="start">
                      <HStack spacing={2} color="gray.900">
                        <Icon as={FaHome} boxSize={3} />
                        <Text fontWeight="medium" fontSize="xs">
                          {shipment.contactDetails.receiverName}
                        </Text>
                      </HStack>
                      <HStack spacing={2} color="gray.600">
                        <Icon as={FaMapMarkerAlt} boxSize={3} />
                        <Text fontSize="xs">{shipment.destinationAddress}</Text>
                      </HStack>
                      <HStack spacing={2} color="gray.500">
                        <Icon as={FaPhone} boxSize={3} />
                        <Text fontSize="xs">
                          {shipment.contactDetails.receiverPhone}
                        </Text>
                      </HStack>
                    </VStack>
                  </DetailSection>

                  <DetailSection
                    title="Delivery Instructions"
                    icon={FaFileAlt}
                    iconColor="purple.600"
                  >
                    <Text fontSize="xs" color="gray.600">
                      {shipment.deliveryInstructions ||
                        "No special instructions"}
                    </Text>
                  </DetailSection>
                </VStack>
              </Grid>
            </TabPanel>

            {/* Tracking Tab */}
            <TabPanel p={6}>
              <VStack spacing={6} align="stretch">
                <DetailSection
                  title="Shipment Journey"
                  icon={FaRouteIcon}
                  data={{ lastUpdatedAt: shipment.lastUpdatedAt }}
                >
                  <Box position="relative">
                    {shipment.statusHistory.map((event, index) => (
                      <TimelineEvent
                        key={index}
                        event={event}
                        index={index}
                        isLatest={index === 0 && (event.hasData || false)}
                      />
                    ))}

                    {shipment.statusHistory.length === 0 && (
                      <Box textAlign="center" py={8}>
                        <Icon
                          as={FaClock}
                          boxSize={8}
                          color="gray.300"
                          mb={3}
                        />
                        <Text color="gray.500" fontSize="sm">
                          No timeline events available
                        </Text>
                      </Box>
                    )}
                  </Box>
                </DetailSection>
              </VStack>
            </TabPanel>

            {/* Orders Tab */}
            <TabPanel p={6}>
              <VStack spacing={4} align="stretch">
                <Flex alignItems="center" justify="space-between" mb={4}>
                  <HStack spacing={2}>
                    <Icon as={FaFileAlt} color="blue.600" boxSize={5} />
                    <Heading size="xs" color="gray.900">
                      Orders in this Shipment ({shipment.orders.length})
                    </Heading>
                  </HStack>
                </Flex>

                <Box
                  borderWidth="1px"
                  rounded="md"
                  overflow="hidden"
                  display="flex"
                  flexDir="column"
                  flex="1"
                  minH={0}
                >
                  <Box flex="1" overflowY="auto">
                    <Table size="sm" style={{ tableLayout: "fixed" }}>
                      <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                        <Tr>
                          <Th>Order</Th>
                          <Th>Items & Quantity</Th>
                          <Th>Value</Th>
                          <Th>Supplier Invoice</Th>
                          <Th>Garaaz Invoice</Th>
                          <Th>Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {shipment.orders.map((order) => (
                          <OrderTableRow key={order.id} order={order} />
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              </VStack>
            </TabPanel>

            {/* Billing Tab */}
            <TabPanel p={6} h="full">
              <Grid templateColumns="repeat(2, 1fr)" gap={6} h="full" minH={0}>
                <DetailSection
                  title="Shipping Charges Breakdown"
                  icon={FaFileInvoice}
                >
                  <VStack spacing={3} align="stretch">
                    <DetailRow
                      label="Delivery Charges"
                      value={
                        shipment.billingDetails?.shippingCharges
                          ? formatCurrency(
                              shipment.billingDetails.shippingCharges / 100
                            )
                          : "N/A"
                      }
                    />
                    <Divider />
                  </VStack>
                </DetailSection>

                <DetailSection
                  title="Payment Information"
                  icon={FaWallet}
                  iconColor="green.600"
                >
                  <VStack spacing={4} align="stretch">
                    <Grid templateColumns="1fr 1fr" gap={4}>
                      {/* Paid Section */}
                      <Box
                        p={3}
                        bg="green.50"
                        borderRadius="md"
                        border="1px"
                        borderColor="green.200"
                      >
                        <VStack spacing={2} align="start">
                          <HStack spacing={2}>
                            <Icon
                              as={FaCheckCircle}
                              color="green.600"
                              boxSize={4}
                            />
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color="green.800"
                            >
                              Paid Orders ({paymentSummary.paidCount})
                            </Text>
                          </HStack>
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color="green.700"
                          >
                            {formatCurrency(paymentSummary.paidAmount)}
                          </Text>
                        </VStack>
                      </Box>

                      {/* Unpaid Section */}
                      <Box
                        p={3}
                        bg="orange.50"
                        borderRadius="md"
                        border="1px"
                        borderColor="orange.200"
                      >
                        <VStack spacing={2} align="start">
                          <HStack spacing={2}>
                            <Icon as={FaClock} color="orange.600" boxSize={4} />
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color="orange.800"
                            >
                              Unpaid Orders ({paymentSummary.unpaidCount})
                            </Text>
                          </HStack>
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color="orange.700"
                          >
                            {formatCurrency(paymentSummary.unpaidAmount)}
                          </Text>
                        </VStack>
                      </Box>
                    </Grid>

                    <VStack spacing={2} align="stretch">
                      <DetailRow
                        label="Total Invoice Amount"
                        value={
                          shipment.orders.length > 0
                            ? formatCurrency(paymentSummary.totalAmount)
                            : ""
                        }
                      />

                      {/* Per-order payment info */}
                      <Box
                        maxH="200px"
                        overflowY="auto"
                        border="1px"
                        borderColor="gray.100"
                        borderRadius="md"
                        sx={{
                          "&::-webkit-scrollbar": {
                            width: "4px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "#f1f1f1",
                            borderRadius: "2px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#c1c1c1",
                            borderRadius: "2px",
                          },
                        }}
                      >
                        {shipment.orders.map((order, index) => (
                          <Box
                            key={order.id}
                            p={3}
                            borderBottom={
                              index < shipment.orders.length - 1
                                ? "1px"
                                : "none"
                            }
                            borderColor="gray.100"
                            _hover={{ bg: "gray.50" }}
                            transition="background 0.2s"
                          >
                            <VStack spacing={2} align="stretch">
                              <HStack justify="space-between" align="center">
                                <HStack spacing={1}>
                                  <Text
                                    fontSize="xs"
                                    fontWeight="medium"
                                    color="gray.700"
                                  >
                                    {order.orderNo}
                                  </Text>
                                  <GaraazCopyText
                                    text={order.orderNo}
                                    popOverMessage="Order No Copied"
                                  />
                                </HStack>
                                <Badge
                                  colorScheme={
                                    order.invoiceStatus === InvoiceStatus.PAID
                                      ? "green"
                                      : "orange"
                                  }
                                  variant="subtle"
                                  fontSize="xs"
                                >
                                  {order.invoiceStatus}
                                </Badge>
                              </HStack>
                              <HStack justify="space-between" align="center">
                                <Text fontSize="xs" color="gray.600">
                                  Invoice: {order.invoiceNo}
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color="gray.800"
                                  fontWeight="semibold"
                                >
                                  {formatCurrency(Number(order.invoiceAmount))}
                                </Text>
                              </HStack>
                              {order.paymentMode &&
                                order.paymentCollectedBy && (
                                  <HStack spacing={4}>
                                    <Text fontSize="xs" color="gray.500">
                                      Mode: {order.paymentMode}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      By: {order.paymentCollectedBy}
                                    </Text>
                                    {order.paymentCollectedAt && (
                                      <Text fontSize="xs" color="gray.500">
                                        Date:{" "}
                                        {formatISOToLocalDate(
                                          order.paymentCollectedAt,
                                          "datetime"
                                        )}
                                      </Text>
                                    )}
                                  </HStack>
                                )}
                            </VStack>
                          </Box>
                        ))}
                      </Box>
                    </VStack>
                  </VStack>
                </DetailSection>
              </Grid>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Action Buttons */}
        <Box flexShrink={0}>
          <Flex
            justify="space-between"
            alignItems="center"
            p={6}
            borderTop="1px"
            borderColor={borderColor}
            bg={headerBg}
            borderBottomRadius="3xl"
          >
            <HStack spacing={3}>
              <Button
                leftIcon={<FaExternalLinkAlt />}
                colorScheme="blue"
                size="md"
                variant="outline"
                borderRadius="xl"
                _hover={{ transform: "translateY(-1px)", boxShadow: "md" }}
                transition="all 0.2s"
                onClick={handleTrackShipment}
              >
                Track Shipment
              </Button>
              <Button
                leftIcon={<FaShare />}
                colorScheme="purple"
                size="md"
                variant="outline"
                borderRadius="xl"
                _hover={{ transform: "translateY(-1px)", boxShadow: "md" }}
                transition="all 0.2s"
                onClick={handleShareTracking}
              >
                Share Tracking
              </Button>
            </HStack>
            <HStack spacing={2}>
              {onMarkDeliveredShipment &&
                !shipment?.isMultiStopTrip &&
                !shipment?.trackingNo &&
                shipment?.status !== ShipmentStatus.DELIVERED &&
                shipment?.status !== ShipmentStatus.CANCELLED && (
                  <Button
                    leftIcon={<FaCheckCircle />}
                    colorScheme="green"
                    size="md"
                    variant="outline"
                    borderRadius="xl"
                    _hover={{
                      transform: "translateY(-1px)",
                      boxShadow: "md",
                      bg: "green.50",
                    }}
                    transition="all 0.2s"
                    onClick={() => {
                      onMarkDeliveredShipment?.(shipment?.id, !shipment.trackingNo);
                    }}
                  >
                    Mark as Delivered
                  </Button>
                )}

              { onCancelShipment && canCancelShipment && (
                <Button
                  leftIcon={<FaTimes />}
                  colorScheme="orange"
                  size="md"
                  variant="outline"
                  borderRadius="xl"
                  _hover={{
                    transform: "translateY(-1px)",
                    boxShadow: "md",
                    bg: "orange.50",
                  }}
                  transition="all 0.2s"
                  onClick={() => {
                      onCancelShipment(shipment?.id, !shipment.trackingNo);
                  }}
                >
                  {!shipment?.trackingNo
                    ? "Cancel Shipment"
                    : "Mark as Cancelled"}
                </Button>
              )}

              {onDeleteShipment && canDeleteShipment && (
                <Button
                  leftIcon={<FaTrash />}
                  colorScheme="red"
                  size="md"
                  variant="outline"
                  borderRadius="xl"
                  _hover={{
                    transform: "translateY(-1px)",
                    boxShadow: "md",
                    bg: "red.50",
                  }}
                  transition="all 0.2s"
                  onClick={() => {
                    onDeleteShipment(shipment?.id || "");
                  }}
                >
                  Delete Shipment
                </Button>
              )}
            </HStack>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
}
